<template>
  <div class="d-flex flex-column">
    <simple-single-selector
      v-model="$v.form.site_uuid.$model"
      :invalid-feedback="$t('cmrad.invalid_site_name')"
      search-query-route="repositories.sites.index"
      :response-data="({ data }) => data.data"
      :search-query-param="getSiteSearchParam"
      :state="getFieldState('site_uuid')"
      :label="$t('cmrad.site_name')"
      :reduce="({ uuid }) => uuid"
      :loading="!areSitesLoaded"
      :clearable="false"
      :disabled="edit"
      :options="sites"
      search-on-focus
      name-key="name"
      required
      paginate-on-scroll
    >
      <template #selected-option="option">
        <site-selector-option :reference="option.reference" :name="getSiteName(option)" />
      </template>

      <template #option="option">
        <site-selector-option :reference="option.reference" :name="option.name" />
      </template>
    </simple-single-selector>

    <b-row class="no-gutters mb-3">
      <b-col sm class="mr-2">
        <text-input
          v-model="$v.form.reference.$model"
          :label="$t('cmrad.research_project_site_id')"
          :placeholder="$t('cmrad.research_project_site_id_placeholder')"
          :state="getFieldState('reference')"
          :invalid-feedback="$t('cmrad.invalid_site_reference')"
          required
        />
      </b-col>
      <b-col sm class="ml-2">
        <simple-single-selector
          v-model="$v.form.status.$model"
          :options="statusOptions"
          name="status"
          :label="$t('cmrad.site_status')"
          :placeholder="$t('cmrad.site_status')"
          :state="getFieldState('status')"
          :invalid-feedback="$t('cmrad.invalid_site_status')"
          :clearable="false"
          required
        />
      </b-col>
    </b-row>
    <research-project-member-selector
      v-if="projectUuid && repositoryUuid"
      v-model="$v.form.member_uuids.$model"
      :options="editingSiteMemberOptions"
      :label="$t('cmrad.add_new_member')"
      :repository-uuid="repositoryUuid"
      :project-uuid="projectUuid"
      only-signed
      clearable
    />
    <rich-editor-input
      v-show="edit"
      v-model="$v.form.comment.$model"
      :invalid-feedback="$t('cmrad.required_form_field_invalid_feedback')"
      :placeholder="$t('cmrad.research_reason_for_change_placeholder')"
      :label="$t('cmrad.research_reason_for_change_label')"
      :state="getFieldState('comment')"
      :lines="3"
      tag="div"
      required
    />
  </div>
</template>

<script>
  import { required, requiredIf, integer } from 'vuelidate/lib/validators';
  import FormHandlingMixin from '@/mixins/FormHandling';
  import { mapGetters, mapActions } from 'vuex';
  import { validationMixin } from 'vuelidate';
  import { cloneDeep } from 'lodash';

  export default {
    name: 'ResearchProjectSiteBasicForm',

    mixins: [validationMixin, FormHandlingMixin],

    components: {
      ResearchProjectMemberSelector: () => import('@/Molecules/Inputs/ResearchProjectMemberSelector'),
      SimpleSingleSelector: () => import('@/Molecules/SimpleSingleSelector'),
      SiteSelectorOption: () => import('@/Organisms/SiteSelectorOption'),
      RichEditorInput: () => import('@/Molecules/Inputs/RichEditorInput'),
      TextInput: () => import('@/Atoms/Form/Inputs/TextInput'),
    },

    props: {
      value: {
        type: Object,
        default: () => ({}),
      },
      edit: Boolean,
      defaultData: Object,
    },

    data() {
      return {
        tried: false,
        siteUuidSent: false,
        form: cloneDeep(this.value),
        statusOptions: [
          { id: 1, name: 'OnBoarding' },
          { id: 2, name: 'Qualified' },
          { id: 3, name: 'Unqualified' },
        ],
      };
    },

    validations: {
      form: {
        site_uuid: {
          required,
        },
        reference: {
          required,
        },
        status: {
          required,
          integer,
        },
        member_uuids: {},
        comment: {
          required: requiredIf(function () {
            return this.edit === true;
          }),
        },
      },
    },

    watch: {
      formData: {
        deep: true,
        initial: true,
        handler(newValue) {
          this.$emit('input', newValue);

          if (this.edit) {
            this.tried = true;
          }
        },
      },

      '$v.form.$invalid': {
        immediate: true,
        handler(newValue) {
          this.$emit('validation', !newValue);
        },
      },

      'form.research_site_id'(newValue, oldValue) {
        if (newValue && oldValue && newValue !== oldValue) {
          this.form.member_uuids = [];
        }
      },
    },

    computed: {
      ...mapGetters('repositorySites', ['getSites', 'areSitesLoaded']),
      ...mapGetters(['repositoryUuid', 'projectUuid']),

      formData() {
        return this.$v.form.$model;
      },

      sites() {
        return this.areSitesLoaded ? this.getSites(this.repositoryUuid) : [];
      },

      editingSiteMemberOptions() {
        return this.defaultData?.members ?? [];
      },
    },

    methods: {
      ...mapActions('repositorySites', ['loadSites']),

      getSiteName({ name }) {
        if (!this.areSitesLoaded && !this.sites.length > 0) return;
        const site = this.sites.find(({ uuid }) => uuid === name);
        return site?.name ?? name;
      },

      getSiteSearchParam(name = null) {
        const includeUuid = !this.siteUuidSent && this.value.site_uuid;
        if (includeUuid) {
          this.siteUuidSent = true;
        }

        return {
          repository: this.repositoryUuid,
          filter: {
            name,
          },
          ...(includeUuid ? { uuids: [this.value.site_uuid] } : {}),
        };
      },
    },
  };
</script>
